/**
 * Minimalerei Before/After Slider — Phase 3.E.1
 *
 * <ba-slider> Custom Element. Drag a vertical handle (or click anywhere
 * in the viewport) to wipe between two stacked images via clip-path.
 * Supports mouse, touch, and keyboard (arrows ±5%, Shift+arrow ±10%,
 * Home/End for 0/100). aria-valuenow updates on change so screen
 * readers can announce position.
 *
 * The actual visual is driven by the --split-pos CSS variable on the
 * <ba-slider> root; CSS does the clip-path + handle/divider positioning.
 */
class BaSlider extends HTMLElement {
  constructor() {
    super();
    this.position = 50;
    this.isDragging = false;
    this.boundOnMove = this.onMove.bind(this);
    this.boundOnEnd = this.onEnd.bind(this);
  }

  connectedCallback() {
    this.viewport = this.querySelector('.ba-slider__viewport');
    this.handle = this.querySelector('.ba-slider__handle');
    if (!this.viewport || !this.handle) return;

    this.position = parseFloat(this.dataset.defaultPosition) || 50;
    this.updatePosition(this.position);

    this.addEventListener('mousedown', this.onStart.bind(this));
    document.addEventListener('mousemove', this.boundOnMove);
    document.addEventListener('mouseup', this.boundOnEnd);

    this.addEventListener('touchstart', this.onStart.bind(this), { passive: false });
    document.addEventListener('touchmove', this.boundOnMove, { passive: false });
    document.addEventListener('touchend', this.boundOnEnd);

    // Hover-track on devices with a fine pointer: split moves with the
    // cursor without click. Touch devices are unaffected (no hover).
    this.boundHoverTrack = this.onHoverTrack.bind(this);
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.addEventListener('mousemove', this.boundHoverTrack);
    }

    this.handle.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener('mousemove', this.boundOnMove);
    document.removeEventListener('mouseup', this.boundOnEnd);
    document.removeEventListener('touchmove', this.boundOnMove);
    document.removeEventListener('touchend', this.boundOnEnd);
    if (this.boundHoverTrack) {
      this.removeEventListener('mousemove', this.boundHoverTrack);
    }
  }

  onHoverTrack(e) {
    // No-op while a drag is in progress — onMove already handles it.
    if (this.isDragging) return;
    this.updatePosition(this.calculatePosition(e));
  }

  onStart(e) {
    // Only react inside the viewport — meta-row clicks shouldn't start a drag.
    if (!e.target.closest('.ba-slider__viewport')) return;
    e.preventDefault();
    this.isDragging = true;
    // Tap-anywhere: snap to the touched/clicked position immediately.
    this.updatePosition(this.calculatePosition(e));
  }

  onMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();
    this.updatePosition(this.calculatePosition(e));
  }

  onEnd() {
    this.isDragging = false;
  }

  onKeyDown(e) {
    let next = this.position;
    const step = e.shiftKey ? 10 : 5;
    switch (e.key) {
      case 'ArrowLeft':
        next = Math.max(0, this.position - step);
        break;
      case 'ArrowRight':
        next = Math.min(100, this.position + step);
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = 100;
        break;
      default:
        return;
    }
    e.preventDefault();
    this.updatePosition(next);
  }

  calculatePosition(e) {
    const rect = this.viewport.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;
    return Math.max(0, Math.min(100, (x / rect.width) * 100));
  }

  updatePosition(pos) {
    this.position = pos;
    this.style.setProperty('--split-pos', `${pos}%`);
    this.handle.setAttribute('aria-valuenow', Math.round(pos));
  }
}

if (!customElements.get('ba-slider')) {
  customElements.define('ba-slider', BaSlider);
}

/**
 * <ba-comparison> wraps the multi-card grid. On desktop all cards
 * stay visible (CSS handles the grid layout). On mobile the parent
 * collapses to single-card carousel: only the active card renders,
 * a row of chip-buttons under the grid lets the visitor switch.
 *
 * Chips are built at runtime from each card's `.ba-comparison__name`
 * span — no separate Liquid markup needed, so theme-block sub-blocks
 * stay clean.
 */
class BaComparison extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('.ba-comparison__card'));
    if (this.cards.length === 0) return;
    this.cardsList = this.querySelector('.ba-comparison__cards');

    this.buildChips();
    this.setActive(0);
  }

  buildChips() {
    if (!this.cardsList) return;
    const chips = document.createElement('div');
    chips.className = 'ba-comparison__chips';

    this.chipButtons = this.cards.map((card, i) => {
      const nameEl = card.querySelector('.ba-comparison__name');
      const label = nameEl && nameEl.textContent.trim() ? nameEl.textContent.trim() : `${i + 1}`;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ba-comparison__chip';
      btn.dataset.cardIndex = i;
      btn.textContent = label;
      btn.addEventListener('click', () => this.setActive(i));
      chips.appendChild(btn);
      return btn;
    });

    this.cardsList.parentNode.insertBefore(chips, this.cardsList.nextSibling);
  }

  setActive(index) {
    this.cards.forEach((card, i) => {
      card.classList.toggle('is-active', i === index);
    });
    if (!this.chipButtons) return;
    this.chipButtons.forEach((btn, i) => {
      btn.classList.toggle('is-active', i === index);
    });
  }
}

if (!customElements.get('ba-comparison')) {
  customElements.define('ba-comparison', BaComparison);
}
